import { useConvertToModernPagination } from '@/context/services/hooks/usePagination';
import { isSuccess } from '@/context/services/type';
import { ExportFileColumn } from '@/features/export_files/List/type';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import { useMutation } from '@tanstack/react-query';
import {
  CommonError,
  ExportFile,
  useDeleteExportFile,
  useGenerateExportFileUrlHook,
  useSearchExportFilesSuspense,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';
import { FetchExportFilesArgs } from './type';
import { AxiosResponse } from 'axios';

const responseToExportFileColumn = (item: ExportFile): ExportFileColumn => ({
  id: item.id,
  name: item.name,
  byte_size: item.byte_size,
  created_at: item.created_at,
  updated_at: item.updated_at,
  error_message: item.error_message,
  content_type: item.content_type,
  status: item.status,
  office_member: item.office_member,
  can_download: item.can_download,
  can_delete: item.can_delete,
});

export const useExportFilesSearch = (args: FetchExportFilesArgs) => {
  const { page = 1, perPage = 25 } = args;

  const { data, isLoading, refetch, error } = useSearchExportFilesSuspense({
    pagination: {
      per_page: perPage,
      page,
    },
  });

  const pagination: ModernPaginationProps = useConvertToModernPagination(
    data?.data?.pagination
  );

  const result = useMemo(
    () => data?.data.export_files?.map(responseToExportFileColumn) ?? [],
    [data]
  );

  return {
    data: result,
    pagination,
    isLoading,
    refetch,
    error,
  };
};

export const useExportFileDownloadUrl = () => {
  const { t } = useTranslation();
  const generateExportFileUrl = useGenerateExportFileUrlHook();

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: (fileId: string) => generateExportFileUrl(fileId),
  });

  const generateDownloadUrl = useCallback(
    async (fileId: string) => {
      const response = await mutateAsync(fileId);

      if (isSuccess(response.status)) {
        return {
          status: 'success',
          presignedUrl: response.data.presigned_url,
        };
      }
      return isCommonError(response.data)
        ? {
            status: 'error',
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            errors: (response.data ?? {}) as CommonError,
          }
        : {
            status: 'invalid',
            errors: { messages: [t('system_error')] },
          };
    },
    [mutateAsync, t]
  );

  return {
    generateDownloadUrl,
    isLoading: isPending,
    error,
  };
};

type DeleteExportFileResult = {
  status: 'success' | 'error' | 'invalid';
  errors?: CommonError | { data: CommonError } | AxiosResponse;
};

export const useDeleteExportFileService = () => {
  const { mutateAsync, error } = useDeleteExportFile();
  const deleteExportFile = useCallback(
    async (exportFileId: string = ''): Promise<DeleteExportFileResult> => {
      // allow res data on method delete
      const response = await mutateAsync({ id: exportFileId });
      if (isSuccess(response.status)) {
        return {
          status: 'success',
        };
      }
      return {
        status: 'error',
        errors: response ?? {},
      };
    },
    [mutateAsync]
  );
  return {
    deleteExportFile,
    error,
  };
};
